import React from 'react';
import {StateSwitchComponent} from '../StateSwitchComponent';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Slider from '@mui/material/Slider';
import fetch from 'cross-fetch'

const TRAIT_FIELDS = [
  { key: 'height', label: 'Height (inches)', id: 'Height', helperText: 'Ex. 77.5', autoComplete: 'Height' },
  { key: 'weight', label: 'Weight (lbs)', id: 'Weight', helperText: 'Ex. 277.5', autoComplete: 'Weight' },
  { key: 'handsize', label: 'Hand Size', id: 'Hand Size', helperText: 'Ex. 9.75', autoComplete: 'Hand Size (inches)' },
  { key: 'armlength', label: 'Arm Length (inches)', id: 'Arm Length', helperText: 'Ex. 33.5', autoComplete: 'Arm Length' },
  { key: 'fourtyyarddash', label: '40 Time', id: '40 Time', helperText: 'Ex. 4.46', autoComplete: '40 Time' },
  { key: 'benchpress', label: 'Bench Press', id: 'Bench Press', helperText: 'Ex. 12', autoComplete: 'Bench Press' },
  { key: 'verticalleap', label: 'Vertical Leap (inches)', id: 'Vertical Leap', helperText: 'Ex. 37.5', autoComplete: 'Vertical Leap ' },
  { key: 'broadjump', label: 'Broad Jump (inches)', id: 'Broad Jump', helperText: 'Ex. 125', autoComplete: 'Broad Jump' },
  { key: 'shuttle', label: 'Shuttle Time', id: 'Shuttle Time', helperText: 'Ex. 4.51', autoComplete: 'Shuttle Time' },
  { key: 'threecone', label: '3 Cone', id: '3 Cone', helperText: 'Ex. 7.15', autoComplete: '3 Cone' },
];

const WEIGHT_SLIDER = { min: 0.125, max: 4, step: 0.125 };

function defaultRequiredTraits(orig) {
  const base = {};
  TRAIT_FIELDS.forEach(({ key }) => {
    base[key] = orig.requiredTraits ? !!orig.requiredTraits[key] : false;
  });
  return base;
}

function defaultSliderWeights(orig) {
  const sw = orig.sliderWeights || {};
  const base = {};
  TRAIT_FIELDS.forEach(({ key }) => {
    base[key] = sw[key] != null ? sw[key] : 1;
  });
  return base;
}

class SearchPage extends StateSwitchComponent{
  constructor(props){
    super(props);
    this.mainSiteComponent = props.mainSiteComponent;
    const orig = this.mainSiteComponent.state.originalStateInfo;

    this.state = {
      name:'Player Name',
      position: orig.position || 'QB',
      height: orig.height || '',
      weight: orig.weight ||'',
      handsize: orig.handsize ||'',
      armlength: orig.armlength || '',
      fourtyyarddash: orig.fourtyyarddash ||'',
      benchpress: orig.benchpress ||'',
      verticalleap: orig.verticalleap ||'',
      broadjump: orig.broadjump ||'',
      shuttle: orig.shuttle ||'',
      threecone: orig.threecone ||'',
      year: orig.year ||'2000',
      sliderWeights: defaultSliderWeights(orig),
      requiredTraits: defaultRequiredTraits(orig),
      loading: false,
      searchError: null,
    };
    this._searchRequestId = 0;
  }
  clean(str){
    return str.replace(/[^a-zA-Z ]/g, "").replace(/ +/g, " ")
  }
  search = () => {
    const requestId = this._searchRequestId + 1;
    this._searchRequestId = requestId;
    this.setState({ loading: true, searchError: null });

    const postOptions = {
      method: 'POST',
      headers: {'Content-Type': 'application/json'},
      body: JSON.stringify({
        name: this.state.name,
        position: this.state.position,
        height: this.state.height,
        weight: this.state.weight,
        handsize: this.state.handsize,
        armlength: this.state.armlength,
        fourtyyarddash: this.state.fourtyyarddash,
        benchpress: this.state.benchpress,
        verticalleap: this.state.verticalleap,
        broadjump: this.state.broadjump,
        shuttle: this.state.shuttle,
        threecone: this.state.threecone,
        year: this.state.year,
        sliderWeights: this.state.sliderWeights,
        requiredTraits: this.state.requiredTraits,
      })
    }
    fetch('/api/request', postOptions)
    .then(res => res.json().then(data => ({ ok: res.ok, data })))
    .then(({ ok, data }) => {
      if (this._searchRequestId !== requestId) {
        return;
      }
      if (!ok) {
        throw new Error(data.error || 'Search failed');
      }
      if (this.mainSiteComponent.state.view !== 'search') {
        this.setState({ loading: false });
        return;
      }
      this.mainSiteComponent.setState({
        playerInfo: data.playerinfo,
        percentiles: data.percentiles,
        view: 'results',
        player_percentiles: data.player_percentiles,
        originalinfo: data.originalinfo,
        originalPlayerName: this.state.name,
        originalStateInfo: this.state,
        nameMap: data.nameMap,
        similarityPercents: data.similarityPercents || {},
      })
    })
    .catch((err) => {
      if (this._searchRequestId !== requestId) {
        return;
      }
      this.setState({ loading: false, searchError: err.message || 'Search failed' });
    });
  }

  handleFieldChange = (field) => (event) => {
    const value = event.target.value;
    this.setState({
      [field]: field === 'name' ? this.clean(value) : value,
    });
  }

  handleSliderWeightChange = (field) => (event, value) => {
    this.setState((prev) => ({
      sliderWeights: { ...prev.sliderWeights, [field]: value },
    }));
  }

  handleRequiredTraitChange = (field) => (event) => {
    this.setState((prev) => ({
      requiredTraits: { ...prev.requiredTraits, [field]: event.target.checked },
    }));
  }

  handleYearChange = (event, value) => {
    this.setState({ year: value })
  }

  renderTraitField = ({ key, label, id, helperText, autoComplete }) => {
    return (
      <FormControl key={key}>
        <TextField
          variant="outlined"
          margin="normal"
          name={label}
          label={label}
          id={id}
          type="number"
          value={this.state[key]}
          helperText={helperText}
          autoComplete={autoComplete}
          onChange={this.handleFieldChange(key)}
        />
        <Slider
          value={Number(this.state.sliderWeights[key]) || 1}
          min={WEIGHT_SLIDER.min}
          max={WEIGHT_SLIDER.max}
          step={WEIGHT_SLIDER.step}
          valueLabelDisplay="auto"
          onChange={this.handleSliderWeightChange(key)}
          aria-label="Trait weight"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={this.state.requiredTraits[key]}
              onChange={this.handleRequiredTraitChange(key)}
              size="small"
            />
          }
          label="Required trait"
        />
      </FormControl>
    );
  }

  render() {

    return (
      <div>
      <Box sx={{p: 2, m:2}} flexGrow =" 1"  justifyContent="center" textAlign= "center">

      <Typography variant="h6"  >
      Use The Sliders to Determine the Multiplier of Each Trait. Blank Traits Will Be Given a Multiplier of 0 Automatically.
      </Typography>
      </Box>
         <Box
            maxWidth="xs"
            height="70vh"
            display='flex'
            alignItems="flex-start"
            justifyContent="center"
            flexDirection="row"
            flexWrap = "wrap"
            sx={{columnGap:5}}
            >
          <FormControl>
          <Select
               value={this.state.position}
               autoWidth
               sx={{ m: 1.5, minWidth: 120 }}
               onChange={this.handleFieldChange('position')}
             >
             <MenuItem value="QB">QB</MenuItem>
             <MenuItem value="RB">RB</MenuItem>
             <MenuItem value="WR">WR</MenuItem>
             <MenuItem value="TE">TE</MenuItem>
             <MenuItem value="OL">OL</MenuItem>
             <MenuItem value="OT">OT</MenuItem>
             <MenuItem value="IOL">IOL</MenuItem>
             <MenuItem value="EDGE">EDGE</MenuItem>
             <MenuItem value="DE">DE</MenuItem>
             <MenuItem value="IDL">IDL</MenuItem>
             <MenuItem value="ILB">ILB</MenuItem>
             <MenuItem value="LB">LB</MenuItem>
             <MenuItem value="CB">CB</MenuItem>
             <MenuItem value="S">S</MenuItem>
             <MenuItem value="DB">DB</MenuItem>
            </Select>

          </FormControl>
          <FormControl>
          <TextField
            variant="outlined"
            margin="normal"
            id="Name"
            label="Name"
            name="Name"
            autoComplete="Name"
            value={this.state.name}
            onChange = {this.handleFieldChange('name')}
            autoFocus
          />
          </FormControl>

          {TRAIT_FIELDS.map(this.renderTraitField)}

          <Slider value={Number(this.state.year) || 2000} min={1987} max={2026} valueLabelDisplay="on"
            onChange={this.handleYearChange}
            aria-label="Oldest draft class"
            sx={{m:3}}
            />
           <Typography id="track-false-slider" gutterBottom>
            Use The Slider Above To Determine The Oldest Draft Class To Get Prospects From. Players drafted before this year are excluded.
          </Typography>
          <Button
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              disabled={this.state.loading}
              onClick={this.search}>
              {this.state.loading ? 'Searching…' : 'Search'}
            </Button>
          {this.state.searchError && (
            <Typography color="error" sx={{ mt: 1 }}>
              {this.state.searchError}
            </Typography>
          )}
          </Box>
      </div>
    );
  }
}

export {SearchPage};
